import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Shield, User, Trash2, ChevronDown } from 'lucide-react'

const roleLabels = {
  admin: { label: 'Admin', color: 'text-violet-400 bg-violet-400/10' },
  member: { label: 'Member', color: 'text-blue-400 bg-blue-400/10' },
  social_manager: { label: 'Social Manager', color: 'text-pink-400 bg-pink-400/10' },
  guest: { label: 'Guest', color: 'text-gray-400 bg-gray-400/10' },
}

const Team = () => {
  const { profile: myProfile } = useAuth()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [changingRole, setChangingRole] = useState(null)

  const isAdmin = myProfile?.role === 'admin'

  useEffect(() => {
    fetchMembers()
  }, [])

  const fetchMembers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true })
    setMembers(data || [])
    setLoading(false)
  }

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId)
    await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId)
    await fetchMembers()
    setChangingRole(null)
  }

  const handleRemove = async (userId) => {
    if (!window.confirm('Rimuovere questo membro?')) return
    await supabase.from('profiles').delete().eq('id', userId)
    setMembers(members.filter(m => m.id !== userId))
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Team</h1>
          <p className="text-gray-500 text-sm mt-1">{members.length} membri</p>
        </div>
      </div>

      <div className="space-y-2">
        {members.map((member) => {
          const role = roleLabels[member.role] || roleLabels.guest
          const isMe = member.id === myProfile?.id
          const avatarLetter = (member.nickname || member.full_name || '?')[0].toUpperCase()

          return (
            <div key={member.id} className="flex items-center gap-4 bg-gray-900 rounded-xl px-4 py-3 border border-gray-800">

              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                {member.avatar_url
                  ? <img src={member.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                  : avatarLetter
                }
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-medium truncate">
                    {member.nickname || member.full_name || 'Utente'}
                  </p>
                  {isMe && <span className="text-xs text-gray-500">(tu)</span>}
                </div>
                <p className="text-gray-500 text-xs truncate">{member.full_name}</p>
              </div>

              {/* Ruolo */}
              {isAdmin && !isMe ? (
                <div className="relative">
                  <select
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                    disabled={changingRole === member.id}
                    className="appearance-none bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500 pr-7 cursor-pointer"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="social_manager">Social Manager</option>
                    <option value="guest">Guest</option>
                  </select>
                  <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              ) : (
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${role.color}`}>
                  {role.label}
                </span>
              )}

              {/* Rimuovi */}
              {isAdmin && !isMe && (
                <button
                  onClick={() => handleRemove(member.id)}
                  className="text-gray-600 hover:text-red-400 transition-colors ml-1"
                >
                  <Trash2 size={16} />
                </button>
              )}

              {/* Icona ruolo per se stessi */}
              {isMe && (
                <div className="text-gray-600 ml-1">
                  {member.role === 'admin' ? <Shield size={16} /> : <User size={16} />}
                </div>
              )}

            </div>
          )
        })}
      </div>

      {!isAdmin && (
        <p className="text-gray-600 text-sm mt-6 text-center">Solo gli admin possono gestire i ruoli del team.</p>
      )}
    </div>
  )
}

export default Team